import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';

interface PaginationButtonProps {
  isActive?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
}

const MARGIN = theme.space['2xs'];
const RADIUS = 5.5;
const SIZE = 27.5;

const Layout = styled.button<PaginationButtonProps>`
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: ${toRem(SIZE)};
  height: ${toRem(SIZE)};
  margin-right: ${toRem(MARGIN)};
  border-radius: ${toRem(RADIUS)};
  background-color: transparent;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  color: ${theme.color.neutral[800]};
  transition: background-color ${theme.transition.button.fast};
  cursor: pointer;
  overflow: hidden;

  svg {
    fill: ${theme.color.neutral[800]};
  }

  &:hover {
    background-color: ${theme.color.neutral[200]};
    color: ${theme.color.neutral[900]};

    svg {
      fill: ${theme.color.neutral[900]};
    }
  }

  &:last-child {
    margin-right: 0;
  }

  ${({ isActive }) =>
    isActive &&
    css`
      background-color: ${theme.color.secondary[400]};
      font-weight: ${theme.font.weight.semiBold};
      color: ${theme.color.white};
      cursor: default;

      &:hover {
        background-color: ${theme.color.secondary[400]};
        color: ${theme.color.white};

        svg {
          fill: ${theme.color.neutral[800]};
        }
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: transparent;
      color: ${theme.color.neutral[400]};
      cursor: default;

      svg {
        fill: ${theme.color.neutral[400]};
      }

      &:hover {
        background-color: transparent;
        color: ${theme.color.neutral[400]};

        svg {
          fill: ${theme.color.neutral[400]};
        }
      }
    `}
`;

const Loading = styled(Skeleton)`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  width: 100%;
  height: 100%;
`;

const PaginationButton: React.FC<PaginationButtonProps> = ({ isActive, disabled, isLoading, onClick, children, className, ...rest }) => (
  <Layout isActive={isActive} disabled={disabled} onClick={onClick} aria-current={isActive ? 'page' : undefined} className={className} {...rest}>
    {isLoading ? <Loading /> : children}
  </Layout>
);

export default PaginationButton;
